import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  List,
  ListItem,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  TextField,
  IconButton,
  Button,
  CircularProgress,
  Alert,
  Badge,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
  Snackbar,
  Tooltip,
} from '@mui/material';
import {
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
  MarkEmailUnread as MarkEmailUnreadIcon,
} from '@mui/icons-material';
import { useConversationsListQuery } from '../hooks/useConversationsListQuery';
import { useConversationDetailQuery } from '../hooks/useConversationDetailQuery';
import { useConversationsUnreadCount } from '../hooks/useConversationsUnreadCount';
import { invalidateMessagingCaches } from '../lib/messagingQueryCache';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import apiService from '../services/api';
import { getAnnouncementImageUrl } from '../utils/imageUrl';
import FoundObjectMessagesWorkspace from '../components/FoundObjectMessagesWorkspace';
import {
  getFoundObjectIdFromConversation,
  isConversationHiddenBecauseRestituted,
  isConversationInRestoredInbox,
} from '../utils/foundObjectConversation';
import { formatFrenchDateTimeInline } from '../utils/formatDateTimeFr';
import {
  ConversationAvatar,
  getConversationListButtonSx,
} from '../components/conversation/ConversationChatUi';

function firstImageUrl(images?: string[] | null): string | null {
  const p = images?.find((x) => x && String(x).trim());
  return p ? getAnnouncementImageUrl(p) : null;
}

const MessagesPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const conversationParam = searchParams.get('conversation');
  const announcementParam = searchParams.get('announcement');
  const foundObjectParam = searchParams.get('found_object');

  const selectedId = conversationParam ? Number.parseInt(conversationParam, 10) : null;
  const [startError, setStartError] = useState<string | null>(null);
  const [startingConversation, setStartingConversation] = useState(false);
  const openingRef = useRef(false);
  const [listFilter, setListFilter] = useState<'pending' | 'unread' | 'all' | 'archived' | 'restored'>('all');
  const [sortOrder, setSortOrder] = useState<'recent' | 'oldest'>('oldest');
  const [searchText, setSearchText] = useState('');
  const [actionSnackbar, setActionSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });
  const [rowActionId, setRowActionId] = useState<number | null>(null);

  const showArchived = listFilter === 'archived';
  const showRestored = listFilter === 'restored';
  const hasSearch = searchText.trim().length > 0;
  // Une seule source : tous les fils, puis filtrage côté client (compteurs = liste).
  const { data: convData, isLoading: loadingList } = useConversationsListQuery({
    poll: true,
    all: true,
  });

  const { data: unreadBadgeData } = useConversationsUnreadCount({ poll: true });

  const {
    data: detailData,
    isLoading: loadingDetail,
    error: detailError,
  } = useConversationDetailQuery({
    conversationId: selectedId,
    poll: true,
  });

  const archiveMutation = useMutation({
    mutationFn: (id: number) => {
      const row = (convData?.conversations ?? []).find((c: any) => c.id === id);
      return row?.is_archived
        ? apiService.unarchiveConversation(id)
        : apiService.archiveConversation(id);
    },
    onMutate: (id) => {
      setRowActionId(id);
    },
    onSuccess: (res, id) => {
      setRowActionId(null);
      if (selectedId === id) {
        setSearchParams({});
      }
      void invalidateMessagingCaches(queryClient);
      setActionSnackbar({ open: true, message: res.message, severity: 'success' });
    },
    onError: (_err, id) => {
      setRowActionId(null);
      const row = (convData?.conversations ?? []).find((c: any) => c.id === id);
      setActionSnackbar({
        open: true,
        message: row?.is_archived
          ? 'Impossible de désarchiver la conversation.'
          : 'Impossible d’archiver la conversation.',
        severity: 'error',
      });
    },
  });

  const markUnreadMutation = useMutation({
    mutationFn: (id: number) => apiService.markConversationUnread(id),
    onMutate: (id) => {
      setRowActionId(id);
    },
    onSuccess: (res, id) => {
      setRowActionId(null);
      if (selectedId === id) {
        setSearchParams({});
      }
      void invalidateMessagingCaches(queryClient);
      setActionSnackbar({ open: true, message: res.message, severity: 'success' });
    },
    onError: () => {
      setRowActionId(null);
      setActionSnackbar({
        open: true,
        message: 'Impossible de marquer la conversation comme non lue.',
        severity: 'error',
      });
    },
  });

  const markTreatedMutation = useMutation({
    mutationFn: (id: number) => apiService.markConversationTreated(id),
    onMutate: (id) => {
      setRowActionId(id);
    },
    onSuccess: (res) => {
      setRowActionId(null);
      void invalidateMessagingCaches(queryClient);
      setActionSnackbar({ open: true, message: res.message, severity: 'success' });
    },
    onError: () => {
      setRowActionId(null);
      setActionSnackbar({
        open: true,
        message: 'Impossible de marquer la conversation comme traitée.',
        severity: 'error',
      });
    },
  });

  const selectedFoundObjectId = foundObjectParam ? Number.parseInt(foundObjectParam, 10) : null;

  useEffect(() => {
    const aid = announcementParam ? Number.parseInt(announcementParam, 10) : null;
    if (!aid || Number.isNaN(aid) || conversationParam) return;
    if (openingRef.current) return;
    openingRef.current = true;
    setStartingConversation(true);
    setStartError(null);
    apiService
      .startConversation(aid)
      .then((data) => {
        openingRef.current = false;
        setStartingConversation(false);
        setSearchParams({ conversation: String(data.conversation_id) });
        void invalidateMessagingCaches(queryClient);
        queryClient.invalidateQueries({ queryKey: ['conversation', data.conversation_id] });
      })
      .catch((err: any) => {
        openingRef.current = false;
        setStartingConversation(false);
        setStartError(err?.response?.data?.error || 'Impossible de démarrer la conversation.');
      });
  }, [announcementParam, conversationParam, setSearchParams, queryClient]);

  const allConversations = convData?.conversations ?? [];

  const isPendingReply = (c: any): boolean => {
    const foStatus = c.announcement?.found_object_status;
    // Restitués / livraison : uniquement si l’API confirme (fil du payeur).
    if (
      foStatus === 'claimed' ||
      foStatus === 'to_ship' ||
      foStatus === 'in_transit' ||
      foStatus === 'disposed'
    ) {
      return c.pending_reply === true;
    }
    if (typeof c.pending_reply === 'boolean') return c.pending_reply;
    return Boolean(c.last_message && !c.last_message.is_from_me);
  };

  const activeInboxConversations = useMemo(
    () =>
      allConversations.filter(
        (c: any) => !c.is_archived && !isConversationHiddenBecauseRestituted(c)
      ),
    [allConversations]
  );
  const restoredConversations = useMemo(
    () =>
      allConversations.filter(
        (c: any) => !c.is_archived && isConversationInRestoredInbox(c)
      ),
    [allConversations]
  );
  const archivedConversations = useMemo(
    () => allConversations.filter((c: any) => Boolean(c.is_archived)),
    [allConversations]
  );

  const pendingConversations = useMemo(
    () => allConversations.filter((c: any) => !c.is_archived && isPendingReply(c)),
    [allConversations]
  );
  const unreadConversations = useMemo(
    () => allConversations.filter((c: any) => (c.unread_count ?? 0) > 0),
    [allConversations]
  );

  const totalUnread = unreadBadgeData?.count ?? unreadConversations.reduce(
    (acc: number, c: any) => acc + (c.unread_count ?? 0),
    0
  );
  const pendingCount = pendingConversations.length;
  const unreadCount = unreadConversations.length;

  const filteredConversations = useMemo(() => {
    const normalizedSearch = searchText.trim().toLowerCase();
    let result: any[];

    if (hasSearch) {
      result = [...allConversations];
    } else if (listFilter === 'pending') {
      result = [...pendingConversations];
    } else if (listFilter === 'unread') {
      result = [...unreadConversations];
    } else if (listFilter === 'archived') {
      result = [...archivedConversations];
    } else if (listFilter === 'restored') {
      result = [...restoredConversations];
    } else {
      result = [...activeInboxConversations];
    }

    if (normalizedSearch) {
      result = result.filter((c: any) => {
        const fullName = `${c.other_user?.first_name ?? ''} ${c.other_user?.last_name ?? ''}`.toLowerCase();
        const email = String(c.other_user?.email ?? '').toLowerCase();
        const title = String(c.announcement?.title ?? '').toLowerCase();
        const preview = String(c.last_message?.content ?? '').toLowerCase();
        const status = String(c.announcement?.found_object_status ?? '').toLowerCase();
        const ref = String(c.announcement?.reference_code ?? '').toLowerCase();
        return (
          fullName.includes(normalizedSearch) ||
          email.includes(normalizedSearch) ||
          title.includes(normalizedSearch) ||
          preview.includes(normalizedSearch) ||
          status.includes(normalizedSearch) ||
          ref.includes(normalizedSearch)
        );
      });
    }

    result.sort((a: any, b: any) => {
      const ta = new Date(a.updated_at ?? 0).getTime();
      const tb = new Date(b.updated_at ?? 0).getTime();
      return sortOrder === 'recent' ? tb - ta : ta - tb;
    });

    return result;
  }, [
    allConversations,
    activeInboxConversations,
    restoredConversations,
    archivedConversations,
    pendingConversations,
    unreadConversations,
    listFilter,
    searchText,
    sortOrder,
    hasSearch,
  ]);

  /** Base liste pour ouvrir une conversation (hors filtre recherche). */
  const conversations = hasSearch
    ? allConversations
    : listFilter === 'pending'
      ? pendingConversations
      : listFilter === 'unread'
        ? unreadConversations
        : listFilter === 'archived'
          ? archivedConversations
          : listFilter === 'restored'
            ? restoredConversations
            : activeInboxConversations;

  const showStatusHints =
    hasSearch || listFilter === 'pending' || listFilter === 'unread' || showRestored;

  const inferredFoundObjectIdFromConversation = useMemo(() => {
    const attrs = detailData?.conversation?.announcement?.object_attributes;
    const fromAttrs = Number(attrs?.found_object_id);
    if (Number.isFinite(fromAttrs) && fromAttrs > 0) return fromAttrs;

    const ref = String(detailData?.conversation?.announcement?.reference_code ?? '');
    const match = ref.match(/^FO-CONTACT-(\d+)$/);
    if (match?.[1]) return Number.parseInt(match[1], 10);
    return null;
  }, [detailData]);

  const activeFoundObjectId = selectedFoundObjectId ?? inferredFoundObjectIdFromConversation;

  /** Complète l’URL avec `found_object` une fois la conversation chargée (liens directs `?conversation=`). */
  useEffect(() => {
    if (!selectedId || Number.isNaN(selectedId) || foundObjectParam) return;
    const inferred = inferredFoundObjectIdFromConversation;
    if (inferred != null && inferred > 0) {
      setSearchParams(
        { conversation: String(selectedId), found_object: String(inferred) },
        { replace: true }
      );
    }
  }, [selectedId, foundObjectParam, inferredFoundObjectIdFromConversation, setSearchParams]);

  const isObjectMessagingView = Boolean(
    (activeFoundObjectId != null && activeFoundObjectId > 0) || announcementParam
  );

  const handleSelect = (id: number) => {
    const c = conversations.find((x: any) => x.id === id);
    const foId = getFoundObjectIdFromConversation(c);
    const nextParams: Record<string, string> = { conversation: String(id) };
    if (foId != null) {
      nextParams.found_object = String(foId);
    }
    setSearchParams(nextParams);
  };

  const handleWorkspaceConversation = (id: number | null) => {
    if (id == null) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('conversation');
        return next;
      });
      return;
    }
    handleSelect(id);
  };

  const closeMessagingDetail = () => {
    setSearchParams({});
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        gap: { xs: 0, md: 2 },
        flex: { xs: 1, md: 'none' },
        minHeight: { xs: 0, md: 480 },
        height: { xs: '100%', md: 'auto' },
        maxWidth: '100%',
        width: '100%',
      }}
    >
      {!isObjectMessagingView && (
      <Card
        sx={{
          width: '100%',
          maxWidth: '100%',
          flex: { xs: 1, md: 'none' },
          minHeight: { xs: 0, md: 'auto' },
          height: { xs: '100%', md: 'auto' },
          maxHeight: { xs: 'none', md: '78vh' },
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          borderRadius: { xs: 0, md: 2 },
          boxShadow: { xs: 'none', md: undefined },
        }}
      >
        <Box sx={{ p: { xs: 1.5, sm: 2 }, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
          <Typography variant="h6" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
            Messagerie
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            {totalUnread > 0 ? `${totalUnread} non lu(s)` : 'À jour'}
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5, rowGap: 1 }}>
            <Chip
              label={`En attente de réponse ${pendingCount}`}
              color={listFilter === 'pending' ? 'warning' : 'default'}
              variant={listFilter === 'pending' ? 'filled' : 'outlined'}
              onClick={() => setListFilter('pending')}
              clickable
            />
            <Chip
              label={`Non lus ${unreadCount}`}
              color={listFilter === 'unread' ? 'error' : 'default'}
              variant={listFilter === 'unread' ? 'filled' : 'outlined'}
              onClick={() => setListFilter('unread')}
              clickable
            />
            <Chip
              label={`Tous ${activeInboxConversations.length}`}
              color={listFilter === 'all' ? 'primary' : 'default'}
              variant={listFilter === 'all' ? 'filled' : 'outlined'}
              onClick={() => setListFilter('all')}
              clickable
            />
            <Chip
              label={`Restitués ${restoredConversations.length}`}
              color={listFilter === 'restored' ? 'success' : 'default'}
              variant={listFilter === 'restored' ? 'filled' : 'outlined'}
              onClick={() => setListFilter('restored')}
              clickable
            />
            <Chip
              label={`Archivées ${archivedConversations.length}`}
              color={listFilter === 'archived' ? 'secondary' : 'default'}
              variant={listFilter === 'archived' ? 'filled' : 'outlined'}
              onClick={() => setListFilter('archived')}
              clickable
            />
          </Stack>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              gap: 1,
              mb: 1.5,
              alignItems: { xs: 'stretch', sm: 'center' },
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Trier par :
            </Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={sortOrder}
              onChange={(_, value) => value && setSortOrder(value)}
              sx={{ alignSelf: { xs: 'stretch', sm: 'auto' } }}
            >
              <ToggleButton value="recent">Plus récent</ToggleButton>
              <ToggleButton value="oldest">Plus ancien</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <TextField
            fullWidth
            size="small"
            placeholder="Rechercher dans les messages..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </Box>
        {loadingList ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4, flex: 1 }}>
            <CircularProgress size={28} />
          </Box>
        ) : filteredConversations.length === 0 ? (
          <Box sx={{ p: 2, flex: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {hasSearch
                ? 'Aucun résultat pour cette recherche.'
                : showArchived
                  ? 'Aucune conversation archivée.'
                  : showRestored
                    ? 'Aucune conversation restituée ou en livraison.'
                    : 'Aucun message pour ce filtre.'}
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 1, flex: 1, minHeight: 0, overflow: 'auto' }}>
            {filteredConversations.map((c: any) => {
              const rowBusy = rowActionId === c.id;
              return (
              <ListItem
                key={c.id}
                disablePadding
                sx={{ mb: 0.75 }}
                secondaryAction={
                  <Stack direction="row" spacing={0.25} alignItems="center" sx={{ pr: 0.5 }}>
                    {(listFilter === 'pending' || c.pending_reply === true) && (
                      <Tooltip title="Plus besoin de répondre (ex. « merci »)">
                        <span>
                          <Button
                            size="small"
                            color="success"
                            variant="outlined"
                            disabled={rowBusy || markTreatedMutation.isPending}
                            onClick={(e) => {
                              e.stopPropagation();
                              markTreatedMutation.mutate(c.id);
                            }}
                            sx={{
                              textTransform: 'none',
                              minWidth: 0,
                              px: 1,
                              py: 0.25,
                              fontSize: '0.75rem',
                              mr: 0.25,
                            }}
                          >
                            Traité
                          </Button>
                        </span>
                      </Tooltip>
                    )}
                    <Tooltip title="Marquer comme non lu">
                      <span>
                        <IconButton
                          edge="end"
                          size="small"
                          aria-label="Marquer comme non lu"
                          disabled={rowBusy || markUnreadMutation.isPending}
                          onClick={(e) => {
                            e.stopPropagation();
                            markUnreadMutation.mutate(c.id);
                          }}
                        >
                          <MarkEmailUnreadIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={c.is_archived ? 'Désarchiver' : 'Archiver'}>
                      <span>
                        <IconButton
                          edge="end"
                          size="small"
                          aria-label={c.is_archived ? 'Désarchiver' : 'Archiver'}
                          disabled={rowBusy || archiveMutation.isPending}
                          onClick={(e) => {
                            e.stopPropagation();
                            archiveMutation.mutate(c.id);
                          }}
                        >
                          {c.is_archived ? (
                            <UnarchiveIcon fontSize="small" />
                          ) : (
                            <ArchiveIcon fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                }
              >
                <ListItemButton
                  selected={selectedId === c.id}
                  onClick={() => handleSelect(c.id)}
                  sx={{
                    borderRadius: 2,
                    alignItems: 'flex-start',
                    pr: { xs: 10, sm: 12 },
                    ...getConversationListButtonSx(c.id, selectedId === c.id),
                  }}
                >
                  <ListItemAvatar>
                    <Badge
                      color="error"
                      variant="dot"
                      invisible={!c.unread_count || c.unread_count === 0}
                      overlap="circular"
                    >
                      <ConversationAvatar
                        conversationId={c.id}
                        imageSrc={firstImageUrl(c.announcement?.images)}
                        initial={c.other_user?.first_name}
                      />
                    </Badge>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                        <Typography variant="subtitle2" noWrap>
                          {c.other_user ? `${c.other_user.first_name} ${c.other_user.last_name}` : 'Utilisateur'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                          {formatFrenchDateTimeInline(
                            c.last_message?.created_at ?? c.updated_at
                          )}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} noWrap>
                          {c.announcement?.title || 'Annonce'}
                          {c.is_archived
                            ? ' · Archivée'
                            : showStatusHints
                              ? c.announcement?.found_object_status
                                ? ` · ${
                                    c.announcement.found_object_status === 'claimed'
                                      ? 'Restitué'
                                      : c.announcement.found_object_status === 'in_transit'
                                        ? 'En livraison'
                                        : c.announcement.found_object_status === 'to_ship'
                                          ? 'À expédier'
                                          : c.announcement.found_object_status === 'disposed'
                                            ? 'Éliminé'
                                            : c.announcement.found_object_status
                                  }`
                                : ''
                              : ''}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {c.last_message?.content || '…'}
                        </Typography>
                      </>
                    }
                  />
                </ListItemButton>
              </ListItem>
              );
            })}
          </List>
        )}
      </Card>
      )}

      <Snackbar
        open={actionSnackbar.open}
        autoHideDuration={4000}
        onClose={() => setActionSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={actionSnackbar.severity}
          onClose={() => setActionSnackbar((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {actionSnackbar.message}
        </Alert>
      </Snackbar>
      {isObjectMessagingView && activeFoundObjectId != null && activeFoundObjectId > 0 && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: (theme) => theme.zIndex.modal - 1,
            bgcolor: 'background.default',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <FoundObjectMessagesWorkspace
              variant="fullscreen"
              foundObjectId={activeFoundObjectId}
              selectedConversationId={selectedId}
              onSelectConversation={handleWorkspaceConversation}
              onClose={closeMessagingDetail}
              preferArchivedList={showArchived}
              preferRestoredList={showRestored}
              onRequestEditFoundObject={() => {
                navigate(`/found-objects?open=${activeFoundObjectId}`);
              }}
              announcementFlow={!!announcementParam}
              conversationPresentInUrl={!!conversationParam}
              startingConversation={startingConversation}
              startError={startError}
              onDismissStartError={() => setStartError(null)}
              onHandoverConfirmed={closeMessagingDetail}
            />
          </Box>
        </Box>
      )}
      {isObjectMessagingView &&
        (!activeFoundObjectId || activeFoundObjectId <= 0) &&
        announcementParam &&
        !conversationParam && (
          <Card sx={{ p: 3, width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={28} />
              <Typography>Préparation de la conversation…</Typography>
            </Box>
          </Card>
        )}
    </Box>
  );
};

export default MessagesPage;
